import { createPersistedStore, storage } from '@connect-x-app/hybrid-utils';
import { Channel, FlightRoomUser } from '@connect-x-app/shared-domain-models';
import { createSerializableStorage, sortFlightroomUsersByRole } from '@connect-x-app/shared-utils';

export type RoomChannels = { customChannels: Channel[]; mainChannel: Channel };

type ChannelState = {
  channelsMap: Map<string, RoomChannels>;
};

type ChannelActions = {
  addUserToChannel: (chatroomId: string, channelId: string, user: FlightRoomUser) => void;
  removeChannelsForRooms: (chatroomIds: string[]) => void;
  removeUserFromChannel: (chatroomId: string, channelId: string, userExternalId: string) => void;
  setChannels: (chatroomId: string, channels: RoomChannels) => void;
  updatePartialCustomChannel: (chatroomId: string, channelId: string, partialChannel: Partial<Channel>) => void;
  updatePartialMainChannel: (chatroomId: string, partialChannel: Partial<Channel>) => void;
};

type ChannelStore = ChannelState & ChannelActions;

const initialState: ChannelState = {
  channelsMap: new Map(),
};

const withSortedUsers = <T extends Partial<Channel>>(channel: T): T =>
  channel.users ? { ...channel, users: sortFlightroomUsersByRole(channel.users) } : channel;

export const useChannelStore = createPersistedStore<ChannelStore>(
  set => ({
    ...initialState,

    addUserToChannel: (chatroomId, channelId, user) =>
      set(state => {
        const room = state.channelsMap.get(chatroomId);
        if (!room) {
          return state;
        }

        if (room.mainChannel.id === channelId) {
          if (room.mainChannel.users.some(u => u.externalId === user.externalId)) {
            return state;
          }
          const newMap = new Map(state.channelsMap);
          newMap.set(chatroomId, {
            ...room,
            mainChannel: { ...room.mainChannel, users: sortFlightroomUsersByRole([...room.mainChannel.users, user]) },
          });
          return { channelsMap: newMap };
        }

        const index = room.customChannels.findIndex(c => c.id === channelId);
        if (index === -1) {
          return state;
        }
        const customChannel = room.customChannels[index];
        if (customChannel.users.some(u => u.externalId === user.externalId)) {
          return state;
        }
        const newCustomChannels = [...room.customChannels];
        newCustomChannels[index] = { ...customChannel, users: sortFlightroomUsersByRole([...customChannel.users, user]) };
        const newMap = new Map(state.channelsMap);
        newMap.set(chatroomId, { ...room, customChannels: newCustomChannels });
        return { channelsMap: newMap };
      }),

    removeChannelsForRooms: chatroomIds =>
      set(state => {
        const newMap = new Map(state.channelsMap);
        let wasChanged = false;
        chatroomIds.forEach(id => {
          if (newMap.has(id)) {
            newMap.delete(id);
            wasChanged = true;
          }
        });

        return wasChanged ? { channelsMap: newMap } : {};
      }),

    removeUserFromChannel: (chatroomId, channelId, userExternalId) =>
      set(state => {
        const room = state.channelsMap.get(chatroomId);
        if (!room) {
          return state;
        }

        if (room.mainChannel.id === channelId) {
          if (!room.mainChannel.users.some(u => u.externalId === userExternalId)) {
            return state;
          }
          const newMap = new Map(state.channelsMap);
          newMap.set(chatroomId, {
            ...room,
            mainChannel: { ...room.mainChannel, users: room.mainChannel.users.filter(u => u.externalId !== userExternalId) },
          });
          return { channelsMap: newMap };
        }

        const index = room.customChannels.findIndex(c => c.id === channelId);
        if (index === -1) {
          return state;
        }
        const customChannel = room.customChannels[index];
        if (!customChannel.users.some(u => u.externalId === userExternalId)) {
          return state;
        }
        const newCustomChannels = [...room.customChannels];
        newCustomChannels[index] = { ...customChannel, users: customChannel.users.filter(u => u.externalId !== userExternalId) };
        const newMap = new Map(state.channelsMap);
        newMap.set(chatroomId, { ...room, customChannels: newCustomChannels });
        return { channelsMap: newMap };
      }),

    setChannels: (chatroomId, { customChannels, mainChannel }) =>
      set(state => {
        const newMap = new Map(state.channelsMap);
        newMap.set(chatroomId, {
          customChannels: customChannels.map(withSortedUsers),
          mainChannel: withSortedUsers(mainChannel),
        });
        return { channelsMap: newMap };
      }),

    updatePartialCustomChannel: (chatroomId, channelId, partialChannel) =>
      set(state => {
        const existingRoomChannels = state.channelsMap.get(chatroomId);
        if (!existingRoomChannels) {
          return state;
        }
        const index = existingRoomChannels.customChannels.findIndex(c => c.id === channelId);
        if (index === -1) {
          return state;
        }
        const newCustomChannels = [...existingRoomChannels.customChannels];
        newCustomChannels[index] = { ...newCustomChannels[index], ...withSortedUsers(partialChannel) };
        const newMap = new Map(state.channelsMap);
        newMap.set(chatroomId, { ...existingRoomChannels, customChannels: newCustomChannels });
        return { channelsMap: newMap };
      }),

    updatePartialMainChannel: (chatroomId, partialChannel) =>
      set(state => {
        const existingRoomChannels = state.channelsMap.get(chatroomId);
        if (!existingRoomChannels) {
          return state;
        }
        const newMap = new Map(state.channelsMap);
        newMap.set(chatroomId, {
          ...existingRoomChannels,
          mainChannel: { ...existingRoomChannels.mainChannel, ...withSortedUsers(partialChannel) },
        });
        return { channelsMap: newMap };
      }),
  }),
  {
    name: 'channels-store',
    storage: createSerializableStorage<ChannelStore>(storage),
  },
);

export const selectRoomChannels = (chatroomId: string): RoomChannels | undefined =>
  useChannelStore.getState().channelsMap.get(chatroomId);
